<template>
  <div :class="$style.menu">
    <form ref="formRef" :action="actionPath" @submit.prevent="closeKeyboard">
      <input id="selling" type="hidden" name="selling" :value="selling" />
      <input id="sort" type="hidden" name="sort" value="popular" />
      <input id="order" type="hidden" name="order" value="asc" />

      <div :class="[$style.section, $style.searchWord]">
        <p :class="$style.sectionTitle">キーワード</p>
        <input id="q" v-model="q" type="text" name="q" enterkeyhint="done" aria-label="キーワード" />
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">カテゴリー</p>
        <select id="category" v-model="category" name="category" class="mb-14" aria-label="カテゴリー" @change="onChangeCategory">
          <option :value="0">選択してください</option>
          <option v-for="cat in categories" :key="cat.categoryId" :value="cat.categoryId">{{ cat.name }}</option>
        </select>
        <select id="sub_category" v-model="subCategory" :disabled="!category" name="sub_category" aria-label="サブカテゴリー">
          <option :value="0">{{ category ? '選択してください' : '-' }}</option>
          <option v-for="subCat in subCategories" :key="subCat.categoryId" :value="subCat.categoryId">{{ subCat.name }}</option>
        </select>
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">価格</p>
        <div :class="$style.priceSortWrap">
          <div :class="$style.priceInputWrap">
            <select id="from_price" v-model="fromPrice" name="from_price" aria-label="下限価格">
              <option value=""></option>
              <option v-for="price in prices" :key="price" :value="price">{{ Number(price).toLocaleString() }}</option>
            </select>
          </div>
          <div>〜</div>
          <div :class="$style.priceInputWrap">
            <select id="to_price" v-model="toPrice" name="to_price" aria-label="上限価格">
              <option value=""></option>
              <option v-for="price in prices" :key="price" :value="price">{{ Number(price).toLocaleString() }}</option>
            </select>
          </div>
        </div>
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">素材</p>
        <select id="material" v-model="material" name="material" aria-label="素材">
          <option value="">すべての素材</option>
          <option v-for="item in materials()" :key="item.materialId" :value="item.slug">{{ item.name }}</option>
        </select>
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">色</p>
        <ul class="color-selection">
          <li v-for="item in colorList" :key="item.colorId">
            <input :id="`color_${item.slug}`" v-model="color" name="color" type="radio" :value="item.slug" />
            <label :for="`color_${item.slug}`" :title="item.name" :aria-label="item.name"><div :class="`color-${item.slug}`"></div></label>
          </li>
        </ul>
      </div>

      <div :class="[$style.section, $style.checkbox]">
        <p>
          <label><input v-model="vintage" type="checkbox" name="vintage" />アンティーク・ヴィンテージ</label>
        </p>
        <p>
          <label><input v-model="gift" type="checkbox" name="gift" />ギフト可</label>
        </p>
        <p>
          <label><input v-model="ordermade" type="checkbox" name="ordermade" />オーダーメイド可</label>
        </p>
        <p>
          <label><input v-model="toggleSelling" type="checkbox" />在庫あり</label>
        </p>
      </div>
      <div :class="$style.submit">
        <button type="button" @click="onClickClearButton">クリア</button>
        <IButton type="primary" size="small" @click="onSubmit">絞り込む</IButton>
        <input v-if="sort" type="hidden" name="sort" :value="sort" />
        <input v-if="order" type="hidden" name="order" :value="order" />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { categories as getCategories } from '@/js/data/category';
import { colors } from '@/js/data/color';
import { materials } from '@/js/data/material';
import IButton from '@/js/components/molecules/IButton.vue';

const props = defineProps<{
  initialQuery: string;
  initialCategory: string;
  initialSubCategory: string;
  initialFromPrice: string;
  initialToPrice: string;
  initialMaterial: string;
  initialColor: string;
  initialGift: string;
  initialOrdermade: string;
  initialVintage: string;
  initialSelling: string;
  sort: string;
  order: string;
}>();
const prices = ['500', '1000', '3000', '5000', '10000', '15000', '20000', '30000', '50000', '100000'];
const formRef = ref<HTMLFormElement>();
const q = ref(props.initialQuery);
const fromPrice = ref(props.initialFromPrice);
const toPrice = ref(props.initialToPrice);
const category = ref(0);
const categories = ref<
  {
    categoryId: number;
    slug: string;
    name: string;
  }[]
>(getCategories({ parentCategoryId: 0 }));
const subCategory = ref(0);
const subCategories = ref<
  {
    categoryId: number;
    slug: string;
    name: string;
  }[]
>();
const material = ref(props.initialMaterial);
const color = ref(props.initialColor);
const colorList = [
  ...colors(),
  {
    colorId: 0,
    slug: 'none',
    name: '選択なし',
  },
];
const gift = ref(props.initialGift === '1');
const ordermade = ref(props.initialOrdermade === '1');
const vintage = ref(props.initialVintage === '1');
const toggleSelling = ref(props.initialSelling === '1');
const categorySlug = computed(() => categories.value?.find((cat) => cat.categoryId === category.value)?.slug || '');
const subCategorySlug = computed(() => subCategories.value?.find((subCat) => subCat.categoryId === subCategory.value)?.slug || '');
const actionPath = computed(() => (categorySlug.value || subCategorySlug.value ? `/category${categorySlug.value ? `/${categorySlug.value}` : ''}${subCategorySlug.value ? `/${subCategorySlug.value}` : ''}` : '/listing'));
const selling = computed(() => (toggleSelling.value ? 'on' : 'off'));
const closeKeyboard = () => {
  const activeElement = document.activeElement as HTMLInputElement;
  if (activeElement) {
    activeElement.blur();
  }
};
const onChangeCategory = () => {
  subCategories.value = getCategories({ parentCategoryId: category.value });
  subCategory.value = 0;
};
const onClickClearButton = () => {
  q.value = '';
  fromPrice.value = '';
  toPrice.value = '';
  material.value = '';
  color.value = '';
  category.value = 0;
  subCategory.value = 0;
  gift.value = false;
  vintage.value = false;
  ordermade.value = false;
  toggleSelling.value = false;
};
const onSubmit = () => {
  formRef.value!.submit();
  if (q.value) {
    window.dataLayer?.push({
      event: 'search',
      search_term: q.value,
    });
  }
};

onMounted(() => {
  const initialCategory = categories.value?.find((cat) => cat.name === props.initialCategory);
  if (initialCategory) {
    category.value = initialCategory.categoryId;
    onChangeCategory();
  }
  const initialSubCategory = subCategories.value?.find((cat) => cat.name === props.initialSubCategory);
  if (initialSubCategory) {
    subCategory.value = initialSubCategory.categoryId;
  }
});
</script>

<style module lang="scss">
.menu {
  @media (--sp) {
    line-height: 1.5;
  }

  form {
    width: 100%;

    @media (--sp) {
      padding: 10px 20px 20px;
    }
  }

  .section {
    margin-bottom: 30px;
  }

  .sectionTitle {
    font-weight: bold;
    margin-bottom: 6px;
  }

  select {
    height: 35px;
    padding-bottom: 6px;
    padding-top: 6px;
    width: 100%;

    @media (--sp) {
      height: 38px;
    }
  }

  .searchWord {
    input[type='text'] {
      text-indent: 0;
      width: 100%;
    }
  }

  .priceSortWrap {
    align-items: center;
    display: flex;
    gap: 4px;
    justify-content: space-between;
  }

  .priceInputWrap {
    flex: 1;
    position: relative;

    @media (--pc) {
      font-size: 12px;
    }

    &::before {
      content: '\00a5';
      left: 5px;
      position: absolute;
      top: 9px;

      @media (--pc) {
        top: 8px;
      }
    }

    select {
      text-indent: 8px;
      width: 100%;

      @media (--pc) {
        font-size: 12px;
        text-indent: 6px;
      }
    }
  }

  .checkbox {
    padding: 16px 0;

    @media (--pc) {
      margin-bottom: 10px;
    }

    p {
      margin-bottom: 10px;

      label {
        align-items: flex-start;
        cursor: pointer;
        display: flex;
        gap: 4px;

        @media (--sp) {
          align-items: center;

          input {
            height: 16px;
            width: 16px;
          }
        }
      }
    }
  }

  .submit {
    display: flex;
    margin-top: 10px;
    text-align: center;

    > button {
      padding: 0 10px 0 0;

      @media (--sp) {
        padding: 0 10px;
      }
    }

    @media (--sp) {
      background-color: var(--color-white);
      bottom: 0;
      box-shadow: 0 -1px 3px 0 var(--color-black-alpha10);
      margin: -20px;
      padding: 12px 10px;
      position: sticky;
      z-index: 1;
    }

    > div {
      flex: 1;
      margin: 0 0 0 10px;

      @media (--sp) {
        margin: 0 10px;
      }
    }
  }
}
</style>
